import React, { useState } from 'react';
import { GiftIcon, AccountIcon } from '../../../public/icons';

const SupportNotificationTab = () => {
  const [futureGiftReminders, setFutureGiftReminders] = useState({
    sevenDays: false,
    threeDays: true,
    oneDay: true
  });

  const [notifications, setNotifications] = useState({
    orderConfirmations: true,
    contentApproval: true,
    fulfillmentAlerts: true,
    lowInventory: true,
    customerSupport: true,
    productReviews: true
  });

  const [templates, setTemplates] = useState({
    orderConfirmation: "Thank you for your order! We've received your request and will begin processing it shortly.",
    supportTicket: "Thank you for contacting Kapta support. We've received your message and will respond within 4 hours."
  });

  const handleReminderToggle = (reminder) => {
    setFutureGiftReminders(prev => ({
      ...prev,
      [reminder]: !prev[reminder]
    }));
  };

  const handleNotificationToggle = (notification) => {
    setNotifications(prev => ({
      ...prev,
      [notification]: !prev[notification]
    }));
  };

  const handleTemplateChange = (template, value) => {
    setTemplates(prev => ({
      ...prev,
      [template]: value
    }));
  };

  const handleSaveChanges = () => {
    console.log('Saving notification preferences:', { futureGiftReminders, notifications });
  };

  const handleSaveTemplates = () => {
    console.log('Saving templates:', templates);
  };

  return (
    <div className="space-y-4 sm:space-y-6 md:space-y-8 px-2 sm:px-0">
      {/* Future Gift Reminders */}
      <div className="bg-white rounded-sm p-4 sm:p-6">
        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <GiftIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#1C2A46]" />
          <div>
            <h2 className="text-lg sm:text-xl font-medium text-[#0E1628]">Future Gift Reminders</h2>
            <p className="text-xs sm:text-sm text-[#6B635D]">Manage your personal admin information.</p>
          </div>
        </div>

        <div className="mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-medium text-[#0E1628] mb-3 sm:mb-4">Remind customers to add content</h3>
          
          <div className="space-y-3 sm:space-y-4">
            {[
              { key: 'sevenDays', label: '7 days before reveal' },
              { key: 'threeDays', label: '3 days before reveal' },
              { key: 'oneDay', label: '1 day before reveal' }
            ].map((reminder) => (
              <label key={reminder.key} className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={futureGiftReminders[reminder.key]}
                  onChange={() => handleReminderToggle(reminder.key)}
                  className="sr-only peer"
                />
                <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-[#6B635D] rounded peer-checked:bg-[#55000C] peer-checked:border-[#55000C] flex items-center justify-center">
                  {futureGiftReminders[reminder.key] && (
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-[#0E1628]">{reminder.label}</span>
              </label>
            ))}
          </div>
        </div>

        <button
          onClick={handleSaveChanges}
          className="w-full sm:w-auto px-5 sm:px-6 md:px-8 py-2 sm:py-3 bg-[#D4A574] text-white font-medium rounded hover:bg-[#C49660] transition-colors text-sm"
        >
          Save Changes
        </button>
      </div>

      {/* Notification Preferences */}
      <div className="bg-white rounded-sm p-4 sm:p-6">
        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <AccountIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#1C2A46]" />
          <div>
            <h2 className="text-lg sm:text-xl font-medium text-[#0E1628]">Notification Preferences</h2>
            <p className="text-xs sm:text-sm text-[#6B635D]">Manage your personal admin information.</p>
          </div>
        </div>

        <div className="space-y-4 sm:space-y-6">
          {[
            { key: 'orderConfirmations', label: 'Order confirmations', description: 'Notify when new orders are placed' },
            { key: 'contentApproval', label: 'Content approval requests', description: 'Notify when content needs review' },
            { key: 'fulfillmentAlerts', label: 'Fulfilment alerts', description: 'Notify about shipping updates' },
            { key: 'lowInventory', label: 'Low inventory warnings', description: 'Alert when stock is running low' },
            { key: 'customerSupport', label: 'Customer support tickets', description: 'Notify when customers submit tickets' },
            { key: 'productReviews', label: 'New product reviews', description: 'Notify when customers leave reviews' }
          ].map((notification) => (
            <div key={notification.key} className="flex flex-col sm:flex-row sm:items-center justify-between py-3 sm:py-4 border-b border-[#F5F5F5] last:border-0 gap-3 sm:gap-0">
              <div className="flex-1">
                <h4 className="font-medium text-[#0E1628] mb-0.5 sm:mb-1 text-sm sm:text-base">{notification.label}</h4>
                <p className="text-xs sm:text-sm text-[#6B635D]">{notification.description}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer self-start">
                <input
                  type="checkbox"
                  checked={notifications[notification.key]}
                  onChange={() => handleNotificationToggle(notification.key)}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 sm:w-11 sm:h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 sm:after:h-5 sm:after:w-5 after:transition-all peer-checked:bg-[#55000C]"></div>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Auto-response Templates */}
      <div className="bg-white rounded-sm p-4 sm:p-6">
        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <AccountIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#1C2A46]" />
          <div>
            <h2 className="text-lg sm:text-xl font-medium text-[#0E1628]">Auto-response Templates</h2>
            <p className="text-xs sm:text-sm text-[#6B635D]">Manage your personal admin information.</p>
          </div>
        </div>

        <div className="space-y-4 sm:space-y-6">
          <div>
            <label className="block text-sm font-medium text-[#0E1628] mb-1 sm:mb-2">Order Confirmation Message</label>
            <textarea
              value={templates.orderConfirmation}
              onChange={(e) => handleTemplateChange('orderConfirmation', e.target.value)}
              rows={4}
              className="w-full p-2 sm:p-3 border border-[#DBDBDB] rounded resize-none focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0E1628] mb-1 sm:mb-2">Support Ticket Auto-Reply</label>
            <textarea
              value={templates.supportTicket}
              onChange={(e) => handleTemplateChange('supportTicket', e.target.value)}
              rows={4}
              className="w-full p-2 sm:p-3 border border-[#DBDBDB] rounded resize-none focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent text-sm"
            />
          </div>
        </div>

        <button
          onClick={handleSaveTemplates}
          className="w-full sm:w-auto mt-4 sm:mt-6 px-5 sm:px-6 md:px-8 py-2 sm:py-3 bg-[#D4A574] text-white font-medium rounded hover:bg-[#C49660] transition-colors text-sm"
        >
          Save Templates
        </button>
      </div>
    </div>
  );
};

export default SupportNotificationTab;